import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import usuariosApi from '../../../api/usuariosApi';

function GestionUsuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsuarios();
  }, []);

  const fetchUsuarios = async () => {
    try {
      setLoading(true);
      const response = await usuariosApi.listar();
      const usuariosData = Array.isArray(response) ? response : [];
      setUsuarios(usuariosData);
      setError(null);
    } catch (error) {
      console.error('Error al obtener los usuarios:', error);
      setError('Error al cargar los usuarios');
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este usuario?')) {
      try {
        await usuariosApi.eliminar(id);
        fetchUsuarios();
      } catch (error) {
        console.error('Error al eliminar el usuario:', error);
        setError('Error al eliminar el usuario');
      }
    }
  };

  const columns = [
    {
      field: 'id_usuario',
      headerName: 'ID',
      width: 80
    },
    {
      field: 'username',
      headerName: 'Usuario',
      width: 200,
      flex: 1
    },
    {
      field: 'nombre_empresa',
      headerName: 'Empresa',
      width: 200,
      flex: 1
    },
    {
      field: 'det_pais',
      headerName: 'País',
      width: 120
    },
    {
      field: 'id_nivel_usuario',
      headerName: 'Nivel',
      width: 150,
      renderCell: (params) => {
        const niveles = {
          1: { label: 'Admin Sistema', color: 'error' },
          2: { label: 'Admin Empresa', color: 'warning' },
          3: { label: 'Supervisor', color: 'info' },
          4: { label: 'Usuario', color: 'default' }
        };
        const nivel = niveles[params.value] || { label: 'N/A', color: 'default' };
        return <Chip label={nivel.label} color={nivel.color} size="small" />;
      }
    },
    {
      field: 'is_active',
      headerName: 'Estado',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Activo' : 'Inactivo'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => navigate(`/admin/usuarios/editar/${params.row.id_usuario}`)}
            title="Editar"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(params.row.id_usuario)}
            title="Eliminar"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Gestión de Usuarios
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/usuarios/crear')}
          sx={{
            background: 'linear-gradient(135deg, #1A237E 0%, #283593 100%)',
            fontWeight: 'bold',
            px: 3
          }}
        >
          Crear Usuario
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={usuarios}
            columns={columns}
            getRowId={(row) => row.id_usuario}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 }
              }
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            checkboxSelection={false}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#F5F5F5',
                fontWeight: 'bold'
              }
            }}
          />
        )}
      </Paper>
    </Container>
  );
}

export default GestionUsuarios;
